// Fatia S3a: campanhas do Google, do Instagram/Facebook, do TikTok e da OpenAI Ads, com saúde e liga/desliga.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useCan, useCompany } from '../../lib/company'
import { Empty, ErrorState, Loading, PageHead, Tabs } from '../../ui'
import { useCampaigns } from '../../features/insights/api'
import { lastSyncText, NoConnections, SyncingBanner } from '../../features/insights/components'
import { CampaignTable } from '../../features/campaigns/CampaignList'

type Filter = 'all' | 'meta' | 'google_ads' | 'tiktok' | 'openai_ads'

export default function Campaigns() {
  const company = useCompany()
  const canCreate = useCan('company.edit_drafts')
  const canConnect = useCan('company.manage_connections')
  const canPublish = useCan('company.publish')
  const [filter, setFilter] = useState<Filter>('all')
  const days = 30
  const q = useCampaigns(company.id, days)

  const head = (
    <PageHead title="Campanhas" sub="Google, Instagram/Facebook, TikTok e OpenAI Ads no mesmo lugar, com a mesma linguagem.">
      {canCreate && <Link to={`/app/${company.id}/criar`} className="btn primary"><Plus size={17} /> Nova campanha</Link>}
    </PageHead>
  )
  if (q.isLoading) return <>{head}<div className="card"><Loading lines={5} /></div></>
  if (q.isError || !q.data) return <>{head}<ErrorState error={q.error} onRetry={() => q.refetch()} /></>
  const d = q.data
  if (!d.hasConnections) return <>{head}<NoConnections companyId={company.id} canConnect={canConnect} /></>

  const providers = new Set(d.items.map((i) => i.provider))
  const list = d.items.filter((c) => filter === 'all' || c.provider === filter)

  return (
    <div className="stack" style={{ gap: 16 }}>
      {head}
      {d.syncing && <SyncingBanner />}
      <div className="row wrap between" style={{ gap: 10 }}>
        <Tabs<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'Todas' },
            ...(providers.has('meta') ? [{ value: 'meta' as const, label: 'Instagram/Facebook' }] : []),
            ...(providers.has('google_ads') ? [{ value: 'google_ads' as const, label: 'Google' }] : []),
            ...(providers.has('tiktok') ? [{ value: 'tiktok' as const, label: 'TikTok' }] : []),
            ...(providers.has('openai_ads') ? [{ value: 'openai_ads' as const, label: 'OpenAI Ads' }] : []),
          ]}
        />
        <span className="small muted">{lastSyncText(d.lastSyncAt)}</span>
      </div>
      {!canPublish && d.items.length > 0 && <p className="small muted">Você pode acompanhar as campanhas, mas ligar, pausar ou mudar orçamento fica com quem gerencia a empresa.</p>}
      {list.length === 0 ? (
        <Empty title={d.syncing ? 'Trazendo suas campanhas…' : 'Nenhuma campanha aqui'}>
          {d.syncing ? 'Em alguns minutos elas aparecem.' : canCreate ? <Link to={`/app/${company.id}/criar`} className="btn primary">Criar campanha</Link> : null}
        </Empty>
      ) : (
        <div className="card flush">
          <CampaignTable companyId={company.id} items={list} days={days} />
        </div>
      )}
    </div>
  )
}
